import React, { useEffect, useState } from "react";
import { useAuth } from "../contexts/AuthContext";
import { authService } from "../services/authService";
import { Save, Lock, Eye, EyeOff } from "lucide-react";
import toast from "react-hot-toast";
import { useNavigate } from "react-router-dom";
import { isLoggingOut, isTokenInvalidationError } from "../utils/errorUtils";

const isStrongPassword = (value: string) => {
  const minLen = value.length >= 8;
  const hasUpper = /[A-Z]/.test(value);
  const hasLower = /[a-z]/.test(value);
  const hasNumber = /\d/.test(value);
  const hasSpecial = /[^A-Za-z0-9]/.test(value);
  return minLen && hasUpper && hasLower && hasNumber && hasSpecial;
};

const Profile: React.FC = () => {
  const { user, refreshUser, logout } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [currentPassword, setCurrentPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isChanging, setIsChanging] = useState(false);
  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  useEffect(() => {
    setName(user?.name || "");
    setEmail(user?.email || "");
    setErrors({});
  }, [user]);

  useEffect(() => {
    if (!newPassword && !confirmPassword) return;

    if (newPassword && confirmPassword && newPassword !== confirmPassword) {
      setErrors((prev) => ({
        ...prev,
        confirmPassword: "Passwords do not match",
      }));
    } else {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.confirmPassword;
        return next;
      });
    }
  }, [newPassword, confirmPassword]);

  useEffect(() => {
    // Check if current and new passwords are the same
    if (currentPassword && newPassword && currentPassword === newPassword) {
      setErrors((prev) => ({
        ...prev,
        newPassword: "New password must be different from current password",
      }));
    } else if (errors.newPassword === "New password must be different from current password") {
      setErrors((prev) => {
        const next = { ...prev };
        delete next.newPassword;
        return next;
      });
    }
  }, [currentPassword, newPassword]);


  const isProfileChanged =
    name.trim() !== "" && name.trim() !== user?.name;


  const isPasswordValid =
    currentPassword &&
    newPassword &&
    confirmPassword &&
    isStrongPassword(newPassword) &&
    newPassword === confirmPassword &&
    currentPassword !== newPassword; // New password must be different from current

  const handleSaveProfile = async () => {
    const nextErrors: Record<string, string> = {};

    if (!name.trim()) {
      nextErrors.name = "Name is required";
    }

    if (name.trim() === user?.name) {
      toast.error("No changes to save");
      return;
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsSaving(true);
      const res = await authService.updateProfile(name.trim());
      toast.success(res.message || "Profile updated");
      await refreshUser();
    } catch (e) {
      toast.error(
        e instanceof Error ? e.message : "Failed to update profile"
      );
    } finally {
      setIsSaving(false);
    }
  };

  const handleChangePassword = async () => {
    const nextErrors: Record<string, string> = {};

    const anyPasswordFilled =
      currentPassword || newPassword || confirmPassword;

    if (!anyPasswordFilled) {
      toast.error("Please fill all password fields");
      return;
    }

    if (!currentPassword) {
      nextErrors.currentPassword = "Current password is required";
    }

    if (!newPassword) {
      nextErrors.newPassword = "New password is required";
    } else if (!isStrongPassword(newPassword)) {
      nextErrors.newPassword =
        "Password must be at least 8 characters and include uppercase, lowercase, number, and special character";
    }

    if (!confirmPassword) {
      nextErrors.confirmPassword = "Confirm password is required";
    } else if (newPassword !== confirmPassword) {
      nextErrors.confirmPassword = "Passwords do not match";
    }

    // Check if current password and new password are the same
    if (currentPassword && newPassword && currentPassword === newPassword) {
      nextErrors.newPassword = "New password must be different from current password";
    }

    if (Object.keys(nextErrors).length > 0) {
      setErrors(nextErrors);
      return;
    }

    try {
      setIsChanging(true);
      const res = await authService.changePassword({
        currentPassword,
        newPassword,
        confirmPassword,
      });

      toast.success(res.message || "Password changed successfully");
      setCurrentPassword("");
      setNewPassword("");
      setConfirmPassword("");
      setErrors({});
      logout();
      navigate("/login");
    } catch (e: any) {
      if (!isTokenInvalidationError(e) && !isLoggingOut()) {
        const errorMessage =
          e?.response?.data?.message ||
          e?.message ||
          "Failed to change password";
        toast.error(errorMessage);
      }
    } finally {
      setIsChanging(false);
    }
  };


  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">My Profile</h1>
      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Email
            </label>
            <input
              type="email"
              value={email}
              disabled
              className="mt-1 block w-full border-gray-200 rounded-md sm:text-sm !bg-gray-100 text-gray-400 cursor-not-allowed"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name
            </label>
            <input
              type="text"
              value={name}
              onChange={(e) => {
                setName(e.target.value);
                if (errors.name) {
                  setErrors((prev) => {
                    const next = { ...prev };
                    delete next.name;
                    return next;
                  });
                }
              }}
              className={`mt-1 block w-full border ${errors.name ? "border-red-500" : "border-gray-300"
                } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
            />
            {errors.name && (
              <p className="mt-1 text-xs text-red-600">{errors.name}</p>
            )}
          </div>
          <button
            onClick={handleSaveProfile}
            disabled={!isProfileChanged || isSaving}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            {isSaving ? "Saving..." : "Save Profile"}
          </button>
        </div>
      </div>

      <div className="bg-white shadow rounded-lg">
        <div className="px-4 py-5 sm:p-6 space-y-4">
          <h3 className="text-lg leading-6 font-medium text-gray-900 flex items-center">
            <Lock className="h-5 w-5 mr-2" />
            Change Password
          </h3>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Current Password
            </label>
            <div className="relative mt-1">
              <input
                type={showCurrent ? "text" : "password"}
                value={currentPassword}
                onChange={(e) => {
                  setCurrentPassword(e.target.value);
                  if (errors.currentPassword) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.currentPassword;
                      return next;
                    });
                  }
                }}
                className={`pl-3 pr-10 block w-full border ${errors.currentPassword ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                onClick={() => setShowCurrent(!showCurrent)}
                aria-label={showCurrent ? "Hide password" : "Show password"}
              >
                {showCurrent ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.currentPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.currentPassword}</p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              New Password
            </label>
            <div className="relative mt-1">
              <input
                type={showNew ? "text" : "password"}
                value={newPassword}
                onChange={(e) => {
                  setNewPassword(e.target.value);
                  if (errors.newPassword) {
                    setErrors((prev) => {
                      const next = { ...prev };
                      delete next.newPassword;
                      return next;
                    });
                  }
                }}
                className={`pl-3 pr-10 block w-full border ${errors.newPassword ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                onClick={() => setShowNew(!showNew)}
                aria-label={showNew ? "Hide password" : "Show password"}
              >
                {showNew ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.newPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.newPassword}</p>
            )}
            {newPassword && !errors.newPassword && !isStrongPassword(newPassword) && (
              <p className="mt-1 text-xs text-yellow-600">
                Password must contain 8+ chars, uppercase, lowercase, number, and special character
              </p>
            )}
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700">
              Confirm Password
            </label>
            <div className="relative mt-1">
              <input
                type={showConfirm ? "text" : "password"}
                value={confirmPassword}
                onChange={(e) => {
                  setConfirmPassword(e.target.value);

                }}
                className={`pl-3 pr-10 block w-full border ${errors.confirmPassword ? "border-red-500" : "border-gray-300"
                  } rounded-md shadow-sm focus:ring-primary-500 focus:border-primary-500 sm:text-sm`}
              />
              <button
                type="button"
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                onClick={() => setShowConfirm(!showConfirm)}
                aria-label={showConfirm ? "Hide password" : "Show password"}
              >
                {showConfirm ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
              </button>
            </div>
            {errors.confirmPassword && (
              <p className="mt-1 text-xs text-red-600">{errors.confirmPassword}</p>
            )}
          </div>
          <button
            onClick={handleChangePassword}
            disabled={!isPasswordValid || isChanging}
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <Save className="h-4 w-4 mr-2" />
            {isChanging ? "Updating..." : "Update Password"}
          </button>
        </div>
      </div>
    </div>
  );
};

export default Profile;
